import { formateInputNumber } from '@leprecoins/utility';
import { Button, ButtonProps, NumberInput, NumberInputProps, useProps } from '@mantine/core';
import { useUncontrolled } from '@mantine/hooks';
import clsx from 'clsx';
import { Delete } from 'lucide-react';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { NumberInput as NumberInputTheme } from '../theme/number-input';
import cls from './calculator.module.scss';

export const CALCULATOR_KEYS = {
	one: '1',
	two: '2',
	three: '3',
	four: '4',
	five: '5',
	six: '6',
	seven: '7',
	eight: '8',
	nine: '9',
	zero: '0',
	delete: 'delete',
	dot: '.',
} as const satisfies Record<string, string>;

export type CalculatorKey = (typeof CALCULATOR_KEYS)[keyof typeof CALCULATOR_KEYS];

export const CALCULATOR_KEYBOARDS = [
	CALCULATOR_KEYS.one,
	CALCULATOR_KEYS.two,
	CALCULATOR_KEYS.three,
	CALCULATOR_KEYS.delete,
	CALCULATOR_KEYS.four,
	CALCULATOR_KEYS.five,
	CALCULATOR_KEYS.six,
	CALCULATOR_KEYS.dot,
	CALCULATOR_KEYS.seven,
	CALCULATOR_KEYS.eight,
	CALCULATOR_KEYS.nine,
	CALCULATOR_KEYS.zero,
] as const satisfies CalculatorKey[];

export type CalculatorClassNames = Partial<
	Record<'root' | 'input' | 'inner' | 'button' | 'footer', string>
>;

export type CalculatorProps = {
	value?: string | number;
	size?: 'sm' | 'md';
	onChange?: (value: string | number) => void;
	numberInputProps?: Omit<NumberInputProps, 'onChange' | 'value'>;
	buttonProps?: Omit<ButtonProps, 'onClick'>;
	classNames?: CalculatorClassNames;
	fastValues?: number[];
} & Omit<ComponentPropsWithoutRef<'div'>, 'onChange' | 'value' | 'defaultValue'>;

export const Calculator = forwardRef<HTMLDivElement, CalculatorProps>(
	(
		{
			value: controlledValue,
			size = 'sm',
			onChange,
			fastValues,
			className,
			classNames,
			numberInputProps,
			buttonProps,
			...props
		},
		ref,
	) => {
		const inputProps = useProps<NumberInputProps>(
			'NumberInput',
			NumberInputTheme.defaultProps,
			numberInputProps ?? {},
		);

		const separator = inputProps?.decimalSeparator ?? '.';

		const [value, setValue] = useUncontrolled({
			value: controlledValue,
			onChange,
			finalValue: '',
			defaultValue: 0,
		});

		const onKeyDown = (key: CalculatorKey) => {
			if (key === CALCULATOR_KEYS.delete) {
				setValue(value.toString().slice(0, -1));

				return;
			}

			if (key === CALCULATOR_KEYS.dot) {
				if (value.toString().includes(separator)) {
					return;
				}

				setValue(value.toString() + separator);
			}

			const newValue = formateInputNumber(
				value.toString() + key,
				inputProps?.max ?? null,
				inputProps?.min ?? null,
				inputProps?.allowDecimal ?? false,
				inputProps?.decimalScale ?? 2,
			);

			setValue(newValue);
		};

		const onFastValueClick = (v: number) => {
			const newValue = formateInputNumber(
				`${Number(value) + v}`,
				inputProps?.max ?? null,
				inputProps?.min ?? null,
				inputProps?.allowDecimal ?? false,
				inputProps?.decimalScale ?? 2,
			);

			setValue(newValue);
		};

		return (
			<div
				ref={ref}
				className={clsx(cls.calculatorRoot, className, classNames?.root)}
				{...props}
			>
				<NumberInput
					value={value}
					className={clsx(cls.calculatorInput, classNames?.input)}
					size={size === 'md' ? '2xl' : 'xl'}
					onChange={setValue}
					{...numberInputProps}
				/>
				<div className={clsx(cls.calculatorInner, classNames?.inner)}>
					{CALCULATOR_KEYBOARDS.map((key) => (
						<Button
							key={key}
							variant='secondary'
							data-key={key}
							className={clsx(cls.calculatorButton, classNames?.button)}
							size={size === 'md' ? 'xl' : 'lg'}
							onClick={() => onKeyDown(key)}
							{...buttonProps}
						>
							{!([CALCULATOR_KEYS.delete, CALCULATOR_KEYS.dot] as CalculatorKey[]).includes(key) &&
								key}
							{key === CALCULATOR_KEYS.dot && separator}
							{key === CALCULATOR_KEYS.delete && <Delete />}
						</Button>
					))}
				</div>
				{fastValues && fastValues.length > 0 && (
					<div className={clsx(cls.calculatorFooter, classNames?.footer)}>
						{fastValues
							.sort((a, b) => a - b)
							.map((v) => (
								<Button
									key={v}
									fullWidth
									size={size === 'md' ? 'xl' : 'lg'}
									className={clsx(cls.calculatorButton, classNames?.button)}
									onClick={() => onFastValueClick(v)}
								>
									{v}
								</Button>
							))}
					</div>
				)}
			</div>
		);
	},
);

Calculator.displayName = 'Calculator';
